import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];

const SessionTimeoutWarning = ({
  // Show warning after this many ms of inactivity (default: 50 min)
  inactivityLimit = 50 * 60 * 1000,
  // Time user has to respond before auto-logout (default: 10 min)
  countdownDuration = 10 * 60 * 1000,
}) => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inactivityTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
  }, []);

  const startInactivityTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);

    inactivityTimerRef.current = setTimeout(() => {
      // Inactivity limit reached — show warning
      const totalSeconds = Math.floor(countdownDuration / 1000);
      setSecondsLeft(totalSeconds);
      setShowWarning(true);

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-logout when countdown expires
      countdownTimerRef.current = setTimeout(() => {
        setShowWarning(false);
        Utilities.triggerSessionExpiry();
      }, countdownDuration);
    }, inactivityLimit);
  }, [inactivityLimit, countdownDuration, clearAllTimers]);

  const handleActivity = useCallback(() => {
    if (!showWarning) {
      startInactivityTimer();
    }
  }, [showWarning, startInactivityTimer]);

  useEffect(() => {
    startInactivityTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [startInactivityTimer, handleActivity, clearAllTimers]);

  const handleStayLoggedIn = async () => {
    setShowWarning(false);
    clearAllTimers();

    const result = await Utilities.refreshTokenWithRetry();
    if (result) {
      startInactivityTimer();
    } else {
      Utilities.triggerSessionExpiry();
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    clearAllTimers();
    Utilities.stopTokenRefreshTimer();
    Utilities.logout(navigate, "access", "refresh", "../signin");
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <Modal
      open={showWarning}
      title={
        <span>
          <ClockCircleOutlined
            style={{ color: "#faad14", marginRight: 8, fontSize: 20 }}
          />
          Session Expiring Soon
        </span>
      }
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      okText="Stay Logged In"
      cancelText="Log Out"
      onOk={handleStayLoggedIn}
      onCancel={handleLogout}
    >
      <p>
        Your session will expire due to inactivity. You will be logged out in{" "}
        <strong>{formatTime(secondsLeft)}</strong>.
      </p>
      <p>Click "Stay Logged In" to continue your session.</p>
    </Modal>
  );
};

export default SessionTimeoutWarning;
